import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../query';
import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { StoreItemResponse, StoreItemsResponse } from '../types';
import { CampaignStoreItemTypes, DynamicCampaignStoreItemOrAthon } from '../types/utils';
import { CampaignSearch, GetCampaignsMeta } from './types';
import { storeItemWithCrowdfundingType } from './utils/storeItemTypes';

const useCurrentCauseQueryString = (causeId?: string) => {
  return `cause_id=${causeId}`;
};

/**
 * Show Campaign using a supplied campaign
 */
function useShowCampaignById_new<
  T extends CampaignStoreItemTypes,
  U = DynamicCampaignStoreItemOrAthon<T>,
>({
  campaignId,
  causeId,
  select,
  queryOptions,
}: {
  campaignId?: string;
  causeId?: string;
  select?: (data: CampaignStoreItemTypes) => U;
  queryOptions?: CampaignSearch;
}) {
  const StoreApi = useStoreApi();
  const causeQuery = useCurrentCauseQueryString(causeId);
  const { querySearchString } = createQueryOptions<CampaignSearch>({
    search: queryOptions,
  });

  const getCampaign = async () => {
    const { data } = await StoreApi.get<StoreItemResponse<CampaignStoreItemTypes>>(
      `/store_items/${campaignId}?${causeQuery}&${querySearchString}`
    );
    return storeItemWithCrowdfundingType(data.store_item);
  };

  // Create a read-only store key
  const storeKey =
    causeId && campaignId ? storeKeys.id(causeId).items.campaigns.id(campaignId).detail : [];
  // Create query key from the store key
  const queryKey = [...storeKey];
  // Dyanmically add the query search string to the query key
  if (querySearchString) queryKey.push(querySearchString);

  return useQuery({
    queryKey: queryKey,
    queryFn: getCampaign,
    enabled: !!campaignId && !!causeId,
    select,
  });
}

const useGetCampaigns = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const getCampaigns = async (context: {
    meta?: GetCampaignsMeta;
  }): Promise<CampaignStoreItemTypes[]> => {
    const { queryString, causeId } = context.meta || {};
    const { data } = await StoreApi.get<StoreItemsResponse<CampaignStoreItemTypes[]>>(
      `/store_items/campaigns?${queryString}`
    );

    data.store_items.forEach((campaign) => {
      if (causeId) {
        queryClient.setQueryData(
          storeKeys.id(causeId).items.campaigns.id(campaign.uuid).detail,
          storeItemWithCrowdfundingType(campaign)
        );
      }
    });
    return data.store_items;
  };
  return getCampaigns;
};

/**
 * Show Campaigns using a supplied cause id, or the cause id from the closest provider
 */
const useListCampaignsByCause = <T = CampaignStoreItemTypes[]>({
  causeId,
  select,
  pagination,
  queryOptions,
  _enabled = true,
}: {
  causeId?: string;
  pagination?: QueryPagination;
  queryOptions?: CampaignSearch;
  select?: (data: CampaignStoreItemTypes[]) => T;
  _enabled?: boolean;
}) => {
  const getCampaigns = useGetCampaigns();
  const causeQuery = useCurrentCauseQueryString(causeId);
  const { queryString, queryObject, queryPagination } = createQueryOptions<CampaignSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });

  const queryKey = causeId ? storeKeys.id(causeId).items.campaigns.all : [];
  return useQuery({
    queryKey: [...queryKey, queryObject, queryPagination],
    queryFn: getCampaigns,
    enabled: !!causeId && _enabled,
    meta: { queryString, causeId },
    select,
    placeholderData: keepPreviousData,
  });
};

const useListCampaignsLive = <T = CampaignStoreItemTypes[]>({
  causeId,
  select,
  pagination,
  queryOptions,
  _enabled = true,
}: {
  causeId?: string;
  pagination?: QueryPagination;
  queryOptions?: CampaignSearch;
  select?: (data: CampaignStoreItemTypes[]) => T;
  _enabled?: boolean;
}) => {
  const queryOptionsSearches = queryOptions?.searches || {};
  const options: CampaignSearch = {
    ...queryOptions,
    searches: { ...queryOptionsSearches, live: { value: true } },
  };
  return useListCampaignsByCause({ causeId, select, pagination, queryOptions: options, _enabled });
};

const useCauseHasCampaigns = ({ causeId }: { causeId?: string }) => {
  return useListCampaignsByCause({ select: (data) => !!data?.length, causeId });
};

const useShowCampaignFinalizationStatus = ({
  causeId,
  campaignId,
}: {
  causeId?: string;
  campaignId?: string;
}) => {
  return useShowCampaignById_new({
    select: (data) => data,
    causeId,
    campaignId,
    queryOptions: {
      options: {
        finalize: true,
      },
    },
  });
};

export {
  useCauseHasCampaigns,
  useListCampaignsByCause,
  useListCampaignsLive,
  useShowCampaignById_new,
  useShowCampaignFinalizationStatus,
};
